import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { Link, useLocation } from "react-router-dom";

import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Table from "../../../components/Table";
import Input from "../../../components/Input";
import InputTextarea from "../../../components/InputTextarea";
import InputSelect from "../../../components/InputSelect";
import Icon from "../../../components/Icon";
import { ModalDelete } from "../../../components/ModalDelete";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { convertIntToMoney } from "../../../helpers/conversions";
import { getEntryStatusLabel } from "../../Finance/helpers";
import { useBillingCompanies } from "../../Finance/billingCompany";
import ModalEntry from "../../Finance/ModalEntry";
import ModalPurchaseInvoiceForm from "../../PurchaseInvoice/ModalPurchaseInvoiceForm";
import ModalLinkPurchaseInvoice from "../ModalLinkPurchaseInvoice";
import { getInvoiceLabel, getInvoicedSummary } from "../../PurchaseInvoice/helpers";
import PurchaseOrderStatus from "../PurchaseOrderStatus";
import {
    PURCHASE_ORDER_STATUS_KEYS,
    fetchPurchaseOrderStatuses,
    getDeliveryLabel,
    getKindLabel,
    isPurchaseOrderCanceled,
} from "../helpers";

const MANUAL_RECIPIENT = '__manual__';
const COMPANY_RECIPIENT = '__company__';

const sameEmail = (a, b) => (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/**
 * Para onde dá para mandar a compra: o e-mail do cadastro da empresa primeiro,
 * depois as pessoas do fornecedor que têm e-mail diferente dele.
 */
const buildRecipients = purchase => {
    const companyEmail = purchase?.company?.email;
    const recipients = companyEmail
        ? [{ value: COMPANY_RECIPIENT, label: `Cadastro da empresa — ${companyEmail}`, email: companyEmail }]
        : [];

    (purchase?.company?.customers ?? []).forEach(contact => {
        if (!contact.email || recipients.some(recipient => sameEmail(recipient.email, contact.email))) return;

        recipients.push({ value: contact.id, label: `${contact.name} — ${contact.email}`, email: contact.email });
    });

    return recipients;
};

/**
 * Detalhe do pedido de compra (BDM-887): itens, entrega e o status, que é
 * cadastrado em Configurações > Status. Ir para "Faturado" gera o título em
 * contas a pagar.
 */
export const ModalPurchaseOrder = ({ show, close, purchaseOrder, canManage, onChanged, onEdit }) => {
    const location = useLocation();
    const [changing, setChanging] = useState(false);
    const [showCancel, setShowCancel] = useState(false);
    const [entries, setEntries] = useState([]);
    const [entryToEdit, setEntryToEdit] = useState(null);
    const [showSend, setShowSend] = useState(false);
    const [sendRecipient, setSendRecipient] = useState(MANUAL_RECIPIENT);
    const [sendEmail, setSendEmail] = useState('');
    const [sendMessage, setSendMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [showEntryModal, setShowEntryModal] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [showInvoiceForm, setShowInvoiceForm] = useState(false);
    const [invoiceToEdit, setInvoiceToEdit] = useState(null);
    const [showLinkInvoice, setShowLinkInvoice] = useState(false);
    const [invoiceToUnlink, setInvoiceToUnlink] = useState(null);
    const [statuses, setStatuses] = useState([]);

    // A situação e o quanto já veio em notas mudam ao lançar uma nota daqui de
    // dentro: o detalhe recarregado vence a linha que veio da listagem.
    const [detail, setDetail] = useState(null);

    // Cadastros que o formulário de título precisa (categoria, centro de custo,
    // conta bancária, empresa de faturamento).
    const [categories, setCategories] = useState([]);
    const [costCenters, setCostCenters] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const { billingCompanies, defaultBillingCompanyId } = useBillingCompanies();

    // A listagem não traz os títulos (são do detalhe), e eles mudam por fora
    // da tela — a automação lança no faturamento, e a baixa acontece no
    // financeiro. Por isso busca ao abrir, em vez de confiar no que veio na
    // linha da lista.
    useEffect(() => {
        if (!show || !purchaseOrder?.id) return;

        setDetail(null);
        setEntries(purchaseOrder.finance_entries ?? []);
        setInvoices(purchaseOrder.purchase_invoices ?? []);
        setSendRecipient(MANUAL_RECIPIENT);
        setSendEmail(purchaseOrder.company_email ?? '');
        loadDetail();

        fetchPurchaseOrderStatuses().then(setStatuses).catch(renderError);
        api.get(`/finance-category`).then(res => setCategories(res.data.resources)).catch(() => {});
        api.get(`/finance-cost-center`).then(res => setCostCenters(res.data.resources)).catch(() => {});
        api.get(`/finance-bank-account`)
            .then(res => setBankAccounts(res.data.resources.filter(account => account.active)))
            .catch(() => {});
    }, [show, purchaseOrder]);

    const loadDetail = () => {
        api.get(`/purchase-order/${purchaseOrder.id}`).then(res => {
            const resource = res.data.resource ?? null;

            setDetail(resource);
            setEntries(resource?.finance_entries ?? []);
            setInvoices(resource?.purchase_invoices ?? []);
            pickRecipient(buildRecipients(resource), resource?.company_email ?? '');
        }).catch(renderError);
    };

    /**
     * Vence o e-mail do último envio. Sem envio, o do cadastro da empresa; sem
     * ele, a pessoa do fornecedor se for uma só — com várias, ninguém é
     * escolhido por ele.
     */
    const pickRecipient = (recipients, lastEmail) => {
        const preferred = lastEmail
            ? recipients.find(recipient => sameEmail(recipient.email, lastEmail))
            : recipients.find(recipient => recipient.value === COMPANY_RECIPIENT)
                ?? (recipients.length === 1 ? recipients[0] : null);

        setSendRecipient(preferred ? preferred.value : MANUAL_RECIPIENT);
        setSendEmail(preferred ? preferred.email : lastEmail);
    };

    if (!purchaseOrder) return null;

    const purchase = detail ?? purchaseOrder;

    // Aberto pela coluna de vínculos do pedido de venda, chega só o id: até o
    // detalhe voltar não há o que mostrar nem em que agir.
    const loaded = detail !== null || purchaseOrder.status !== undefined;
    const isCanceled = isPurchaseOrderCanceled(purchase);
    const invoiced = getInvoicedSummary({ ...purchase, invoiced_amount: invoices.reduce((total, invoice) => total + (invoice.pivot?.amount ?? 0), 0) });
    const hasInvoices = invoices.length > 0;

    const changeStatus = status => {
        setChanging(true);

        api.patch(`/purchase-order/${purchaseOrder.id}/status`, { status_id: status.id }).then(res => {
            const resource = res.data.resource;

            setDetail(current => ({ ...current, ...resource }));
            toast.success(`Status alterado para "${status.name}"`);

            // Faturar pode ter lançado a conta a pagar pela automação.
            if (status.key === PURCHASE_ORDER_STATUS_KEYS.invoiced) loadDetail();
            onChanged();
        }).catch(renderError).then(() => setChanging(false));
    };

    const send = () => {
        setSending(true);

        api.post(`/purchase-order/${purchaseOrder.id}/send`, {
            email: sendEmail,
            message: sendMessage || null,
        }).then(res => {
            toast.success(res.data.message ?? 'Pedido de compra enviado');
            setShowSend(false);
            setSendMessage('');
            loadDetail();
            onChanged();
        }).catch(renderError).then(() => setSending(false));
    };

    const unlinkInvoice = () => {
        setChanging(true);

        api.delete(`/purchase-order/${purchaseOrder.id}/invoice/${invoiceToUnlink.id}`).then(res => {
            toast.success(res.data.message ?? 'Nota desvinculada desta compra');
            setInvoiceToUnlink(null);
            loadDetail();
            onChanged();
        }).catch(renderError).then(() => setChanging(false));
    };

    const createPayable = () => {
        setChanging(true);

        api.post(`/purchase-order/${purchaseOrder.id}/payable`).then(res => {
            toast.success('Conta a pagar lançada');
            setEntries(current => [...current, res.data.resource]);
            onChanged();
        }).catch(renderError).then(() => setChanging(false));
    };

    const cancel = () => {
        setChanging(true);

        api.patch(`/purchase-order/${purchaseOrder.id}/cancel`).then(() => {
            toast.success('Pedido de compra cancelado');
            setShowCancel(false);
            close();
            onChanged();
        }).catch(renderError).then(() => setChanging(false));
    };

    const recipients = buildRecipients(purchase);

    const recipientOptions = [
        ...recipients.map(({ value, label }) => ({ value, label })),
        { label: 'Outro e-mail', value: MANUAL_RECIPIENT },
    ];

    const changeRecipient = value => {
        setSendRecipient(value);

        const recipient = recipients.find(option => option.value === value);

        setSendEmail(recipient ? recipient.email : '');
    };

    const itemRows = (purchase.items ?? []).map(item => ({
        data: [
            item.description,
            item.reference ?? '—',
            item.quantity,
            convertIntToMoney(item.unit_cost, true),
            convertIntToMoney(item.total_cost, true),
        ],
    }));

    return (
        <>
            <ModalEntry
                level={1}
                show={showEntryModal}
                close={() => { setShowEntryModal(false); setEntryToEdit(null); }}
                entry={entryToEdit}
                type={`payable`}
                categories={categories}
                costCenters={costCenters}
                bankAccounts={bankAccounts}
                billingCompanies={billingCompanies}
                defaultBillingCompanyId={defaultBillingCompanyId}
                onSaved={() => { loadDetail(); onChanged(); }}
            />

            <ModalPurchaseInvoiceForm
                level={1}
                show={showInvoiceForm}
                close={() => { setShowInvoiceForm(false); setInvoiceToEdit(null); }}
                invoice={invoiceToEdit}
                purchaseOrder={purchase}
                onSaved={() => { loadDetail(); onChanged(); }}
            />

            <ModalLinkPurchaseInvoice
                level={1}
                show={showLinkInvoice}
                close={() => setShowLinkInvoice(false)}
                purchaseOrder={purchase}
                onLinked={() => { loadDetail(); onChanged(); }}
            />

            <Modal
                level={1}
                className={`purchase-order-unlink-modal`}
                title={`Desvincular nota`}
                show={!!invoiceToUnlink}
                close={() => setInvoiceToUnlink(null)}
                footer={
                    <>
                        <Button type={`secondary`} size={`small`} text={`Cancelar`} action={() => setInvoiceToUnlink(null)} />
                        <Button type={`primary`} size={`small`} svg={`unlink`} text={`Desvincular`} action={unlinkInvoice} loading={changing} />
                    </>
                }
            >
                <div className="section">
                    <p>
                        Tirar a nota <strong>{getInvoiceLabel(invoiceToUnlink)}</strong> deste pedido de compra?
                        Ela continua cadastrada em Notas de entrada, e o título a pagar que saiu dela não é cancelado.
                    </p>
                </div>
            </Modal>

            <Modal
                level={1}
                className={`purchase-order-send-modal`}
                title={`Enviar ao fornecedor`}
                show={showSend}
                close={() => setShowSend(false)}
                footer={
                    <>
                        <Button type={`secondary`} size={`small`} text={`Cancelar`} action={() => setShowSend(false)} />
                        <Button type={`primary`} size={`small`} svg={`send`} text={`Enviar`} action={send} loading={sending} />
                    </>
                }
            >
                <div className="section">
                    <p className={`fiscal-hint`}>
                        O fornecedor recebe o PDF do pedido de compra #{purchase?.code} em anexo.
                        A resposta dele volta para o seu e-mail.
                    </p>
                    {recipientOptions.length > 1 &&
                        <InputSelect
                            label={`Destinatário`}
                            options={recipientOptions}
                            value={sendRecipient}
                            change={changeRecipient}
                        />
                    }
                    {(sendRecipient === MANUAL_RECIPIENT || recipientOptions.length <= 1) &&
                        <Input label={`E-mail do fornecedor`} value={sendEmail} change={setSendEmail} />
                    }
                    <InputTextarea label={`Recado (opcional)`} value={sendMessage} change={setSendMessage} />
                </div>
            </Modal>

            <ModalDelete
                level={1}
                show={showCancel}
                text={<p>Cancelar o pedido de compra <strong>#{purchase.code}</strong>? A conta a pagar em aberto também será cancelada.</p>}
                action={cancel}
                loading={changing}
                close={() => setShowCancel(false)}
            />

            <Modal
                className={`purchase-order-modal`}
                size={`big`}
                title={purchase.code ? `Pedido de compra #${purchase.code}` : `Pedido de compra`}
                show={show}
                close={close}
                footer={!loaded
                    ? <Button type={`secondary`} size={`small`} text={`Fechar`} action={close} />
                    : <>
                        {canManage && !isCanceled &&
                            <Button type={`secondary`} size={`small`} text={`Cancelar compra`} action={() => setShowCancel(true)} />
                        }
                        {canManage && !isCanceled &&
                            <Button type={`secondary`} size={`small`} svg={`edit`} text={`Editar`} action={() => onEdit(purchase)} />
                        }
                        <Button type={`secondary`} size={`small`} svg={`pdf`} text={`PDF`} action={() => window.open(purchase.pdf_url)} />
                        {canManage && !isCanceled &&
                            <Button type={`primary`} size={`small`} svg={`send`} text={`Enviar ao fornecedor`} action={() => setShowSend(true)} />
                        }
                        <Button type={`secondary`} size={`small`} text={`Fechar`} action={close} />
                    </>
                }
            >
                {!loaded &&
                    <div className="section purchase-order-detail">
                        <p className={`fiscal-hint`}>Carregando o pedido de compra...</p>
                    </div>
                }

                {loaded &&
                    <div className="section purchase-order-detail">
                        <div className={`po-block po-summary`}>
                            <div>
                                <span>Fornecedor</span>
                                <strong>{purchase.company_trade_name ?? '—'}</strong>
                            </div>
                            <div>
                                <span>Tipo</span>
                                <strong>{getKindLabel(purchase.kind)}</strong>
                            </div>
                            <div>
                                <span>Valor</span>
                                <strong>{convertIntToMoney(purchase.total, true)}</strong>
                            </div>
                            <div>
                                <span>Entrega em</span>
                                <strong>{getDeliveryLabel(purchase.delivery_target)}</strong>
                            </div>
                            {purchase.order &&
                                <div>
                                    <span>Pedido de venda</span>
                                    <strong>
                                        {location.pathname === `/order/edit/${purchase.order.id}`
                                            ? `#${purchase.order.code}`
                                            : <Link className={`po-summary-link`} to={`/order/edit/${purchase.order.id}`}>#{purchase.order.code}</Link>
                                        }
                                    </strong>
                                </div>
                            }
                        </div>

                        <PurchaseOrderStatus
                            purchase={purchase}
                            statuses={statuses}
                            canManage={canManage}
                            hasInvoices={hasInvoices}
                            changing={changing}
                            onChange={changeStatus}
                        />

                        {(purchase.delivery_street || purchase.delivery_city) &&
                            <div className={`po-block`}>
                                <span className={`po-section-title`}>Endereço de entrega</span>
                                <p className={`po-delivery-address`}>
                                    {[
                                        purchase.delivery_name,
                                        purchase.delivery_street,
                                        purchase.delivery_number,
                                        purchase.delivery_district,
                                        purchase.delivery_city,
                                        purchase.delivery_state,
                                    ].filter(Boolean).join(', ')}
                                </p>
                            </div>
                        }

                        <div className={`po-block`}>
                            <span className={`po-section-title`}>Itens</span>
                            <Table
                                header={['Item', 'Referência', 'Qtd.', 'Custo unit.', 'Total']}
                                rows={itemRows}
                                loading={false}
                            />
                        </div>

                        <div className={`po-block po-invoices`}>
                            <div className={`po-section-header`}>
                                <span className={`po-section-title`}>Notas desta compra</span>
                                {canManage && !isCanceled &&
                                    <span className={`po-section-actions`}>
                                        <Button
                                            type={`secondary`}
                                            size={`small`}
                                            svg={`link`}
                                            text={`Vincular nota`}
                                            action={() => setShowLinkInvoice(true)}
                                        />
                                        <Button
                                            type={`secondary`}
                                            size={`small`}
                                            svg={`plus-blue`}
                                            text={`Lançar nota`}
                                            action={() => { setInvoiceToEdit(null); setShowInvoiceForm(true); }}
                                        />
                                    </span>
                                }
                            </div>

                            {hasInvoices &&
                                <div className={`po-invoiced-progress ${invoiced.over ? 'over' : ''}`}>
                                    <div className={`bar`}>
                                        <span style={{ width: `${invoiced.percent}%` }} />
                                    </div>
                                    <div className={`labels`}>
                                        <span>
                                            Notas lançadas: <strong>{convertIntToMoney(invoiced.invoiced, true)}</strong>
                                            {' de '}{convertIntToMoney(invoiced.total, true)}
                                        </span>
                                        {invoiced.over &&
                                            <span className={`difference`}>
                                                {convertIntToMoney(invoiced.difference, true)} acima do previsto — confira frete e IPI
                                            </span>
                                        }
                                        {invoiced.partial &&
                                            <span className={`difference`}>
                                                faltam {convertIntToMoney(invoiced.total - invoiced.invoiced, true)}
                                            </span>
                                        }
                                    </div>
                                </div>
                            }

                            {!hasInvoices
                                ? <p className={`fiscal-hint`}>
                                    Nenhuma nota lançada ainda.
                                    {canManage && !isCanceled && ' Arraste o XML no formulário e o resto se preenche sozinho.'}
                                </p>
                                : <Table
                                    header={canManage && !isCanceled
                                        ? ['Nota', 'Emissão', 'Valor', 'Anexos', '']
                                        : ['Nota', 'Emissão', 'Valor', 'Anexos']
                                    }
                                    rows={invoices.map(invoice => ({
                                        action: canManage
                                            ? () => { setInvoiceToEdit(invoice); setShowInvoiceForm(true); }
                                            : undefined,
                                        buttons: canManage && !isCanceled
                                            ? [
                                                <Button
                                                    type={`secondary`}
                                                    size={`small`}
                                                    svg={`unlink`}
                                                    action={() => setInvoiceToUnlink(invoice)}
                                                />,
                                            ]
                                            : undefined,
                                        data: [
                                            getInvoiceLabel(invoice),
                                            invoice.issued_at ? moment(invoice.issued_at).format('DD/MM/YYYY') : '—',
                                            convertIntToMoney(invoice.pivot?.amount ?? invoice.amount, true),
                                            <span className={`purchase-invoice-files`}>
                                                {invoice.file_url &&
                                                    <a href={invoice.file_url} target={`_blank`} rel={`noreferrer`} onClick={event => event.stopPropagation()}>
                                                        <Icon name={`pdf`} size={16} />
                                                    </a>
                                                }
                                                {invoice.xml_url &&
                                                    <a href={invoice.xml_url} target={`_blank`} rel={`noreferrer`} onClick={event => event.stopPropagation()}>
                                                        <Icon name={`xml`} size={16} />
                                                    </a>
                                                }
                                                {!invoice.file_url && !invoice.xml_url && '—'}
                                            </span>,
                                        ],
                                    }))}
                                    loading={false}
                                />
                            }
                        </div>

                        <div className={`po-block po-entries`}>
                            <span className={`po-section-title`}>Contas a pagar desta compra</span>

                            {entries.length === 0
                                ? <p className={`fiscal-hint`}>
                                    Nenhum título lançado ainda.
                                    {canManage && !isCanceled && (hasInvoices
                                        ? ' Cada nota lançada vira um título, se a automação estiver ligada.'
                                        : ' Faturar a compra lança sozinho, se a automação estiver ligada.'
                                    )}
                                </p>
                                : <Table
                                    header={['Vencimento', 'Valor', 'Situação', 'Origem']}
                                    rows={entries.map(entry => ({
                                        action: canManage
                                            ? () => { setEntryToEdit(entry); setShowEntryModal(true); }
                                            : undefined,
                                        data: [
                                            moment(entry.due_date).format('DD/MM/YYYY'),
                                            convertIntToMoney(entry.amount, true),
                                            <span className={`finance-entry-status status-${entry.status}`}>
                                                {getEntryStatusLabel(entry.status)}
                                            </span>,
                                            entry.created_by_automation ? 'Automação' : 'Manual',
                                        ],
                                    }))}
                                    loading={false}
                                />
                            }

                            {canManage && !isCanceled && !hasInvoices && entries.length === 0 && purchase.total > 0 &&
                                <Button
                                    type={`secondary`}
                                    size={`small`}
                                    text={`Lançar conta a pagar`}
                                    action={createPayable}
                                    loading={changing}
                                />
                            }
                        </div>
                    </div>
                }
            </Modal>
        </>
    );
};

export default ModalPurchaseOrder;
